import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { VideoProject } from '../types';

export function useProjects(personaId?: string) {
  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await api.getProjects(personaId);

    if (result.error) {
      setError(result.error);
    } else {
      setProjects(result.data?.projects || []);
    }

    setIsLoading(false);
  }, [personaId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { projects, isLoading, error, refetch: fetchProjects };
}

export function useProject(id: string) {
  const [project, setProject] = useState<VideoProject | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    const result = await api.getProject(id);

    if (result.error) {
      setError(result.error);
    } else {
      setProject(result.data?.project || null);
    }

    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const startGeneration = async () => {
    if (!id) return;

    const result = await api.startGeneration(id);

    if (result.error) {
      setError(result.error);
      return false;
    }

    await fetchProject();
    return true;
  };

  const cancelProject = async () => {
    if (!id) return;

    const result = await api.cancelProject(id);

    if (result.error) {
      setError(result.error);
      return false;
    }

    await fetchProject();
    return true;
  };

  return { project, isLoading, error, refetch: fetchProject, startGeneration, cancelProject };
}

export function useProjectStatus(id: string, pollInterval = 3000) {
  const [status, setStatus] = useState<{
    status: string;
    progress: number;
    output_url?: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchStatus = async () => {
      const result = await api.getProjectStatus(id);
      if (!result.error && result.data) {
        setStatus(result.data);
      }
    };

    fetchStatus();

    // Poll if project is in progress
    const interval = setInterval(async () => {
      const result = await api.getProjectStatus(id);
      if (!result.error && result.data) {
        setStatus(result.data);

        // Stop polling if complete or failed
        if (['complete', 'failed', 'cancelled'].includes(result.data.status)) {
          clearInterval(interval);
        }
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [id, pollInterval]);

  return status;
}
